import axios from 'axios';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://bsafbackend.andasy.dev/api'
    : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthRoute = url.includes('/auth/');
    if (error.response?.status === 401 && !isAuthRoute) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data: any) => api.post('/auth/register', data),
  verifyOTP: (data: any) => api.post('/auth/verify-otp', data),
  resendOTP: (data: any) => api.post('/auth/resend-otp', data),
  login: (data: any) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data: any) => api.post('/auth/forgot-password', data),
  resetPassword: (data: any) => api.post('/auth/reset-password', data),
};

export const jobsAPI = {
  getAll: (params?: any) => api.get('/jobs', { params }),
  getMyJobs: (params?: any) => api.get('/jobs/my-jobs', { params }),
  getById: (id: string) => api.get(`/jobs/${id}`),
  getBySlug: (slug: string) => api.get(`/jobs/slug/${slug}`),
  create: (data: any) => api.post('/jobs', data),
  update: (id: string, data: any) => api.put(`/jobs/${id}`, data),
  delete: (id: string) => api.delete(`/jobs/${id}`),
};

export const opportunitiesAPI = {
  getAll: (params?: any) => api.get('/opportunities', { params }),
  getById: (id: string) => api.get(`/opportunities/${id}`),
  getBySlug: (slug: string) => api.get(`/opportunities/slug/${slug}`),
  create: (data: any) => api.post('/opportunities', data),
  update: (id: string, data: any) => {
    const isFormData = data instanceof FormData;
    return api.put(`/opportunities/${id}`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
  },
  delete: (id: string) => api.delete(`/opportunities/${id}`),
};

export const servicesAPI = {
  getAll: (params?: any) => api.get('/services', { params }),
  getById: (id: string) => api.get(`/services/${id}`),
  getBySlug: (slug: string) => api.get(`/services/slug/${slug}`),
  create: (data: any) => api.post('/services', data),
  update: (id: string, data: any) => api.put(`/services/${id}`, data),
  delete: (id: string) => api.delete(`/services/${id}`),
  requestService: (data: FormData) => api.post('/services/request', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyRequests: () => api.get('/services/my-requests'),
};

export const applicationsAPI = {
  apply: (data: FormData) => api.post('/applications/apply', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: (params?: any) => api.get('/applications', { params }),
  getMyApplications: (params?: any) => api.get('/applications/my-applications', { params }),
  getMyJobApplications: (params?: any) => api.get('/applications/my-jobs', { params }),
  getById: (id: string) => api.get(`/applications/${id}`),
  withdraw: (id: string) => api.patch(`/applications/${id}/withdraw`),
  updateStatus: (id: string, data: any) => api.patch(`/applications/${id}/status`, data),
};

export const articlesAPI = {
  getAll: (params?: any) => api.get('/articles', { params }),
  getById: (id: string) => api.get(`/articles/${id}`),
  create: (data: any) => api.post('/articles', data),
  update: (id: string, data: any) => api.put(`/articles/${id}`, data),
  delete: (id: string) => api.delete(`/articles/${id}`),
  publish: (id: string) => api.patch(`/articles/${id}/publish`),
};

export const faqAPI = {
  getAll: (params?: any) => api.get('/faqs', { params }),
  create: (data: any) => api.post('/faqs', data),
  update: (id: string, data: any) => api.put(`/faqs/${id}`, data),
  delete: (id: string) => api.delete(`/faqs/${id}`),
};

export const blogsAPI = {
  getAll: (params?: any) => api.get('/blogs', { params }),
  getTrending: () => api.get('/blogs/trending'),
  getBySlug: (slug: string) => api.get(`/blogs/${slug}`),
  getAllAdmin: (params?: any) => api.get('/blogs/admin/all', { params }),
  create: (data: FormData) => api.post('/blogs', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id: string, data: FormData) => api.put(`/blogs/${id}`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id: string) => api.delete(`/blogs/${id}`),
  publish: (id: string) => api.patch(`/blogs/${id}/publish`),
  toggleTrending: (id: string) => api.patch(`/blogs/${id}/trending`),
};

export const pricingAPI = {
  getAll: (params?: any) => api.get('/pricing', { params }),
  getById: (id: string) => api.get(`/pricing/${id}`),
  create: (data: any) => api.post('/pricing', data),
  update: (id: string, data: any) => api.put(`/pricing/${id}`, data),
  delete: (id: string) => api.delete(`/pricing/${id}`),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  createUser: (data: any) => api.post('/admin/users', data),
  getActivity: (params?: any) => api.get('/admin/activity', { params }),
  getAnalytics: (params?: any) => api.get('/admin/analytics', { params }),
  getUsers: (params?: any) => api.get('/admin/users', { params }),
  updateUser: (id: string, data: any) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id: string) => api.delete(`/admin/users/${id}`),
  manageUser: (userId: string, data: any) => api.patch(`/admin/users/${userId}/manage`, data),
  getEmployerMetrics: () => api.get('/admin/employer-metrics'),
  getJobMetrics: (params?: any) => api.get('/admin/job-metrics', { params }),
  getRevenueMetrics: (params?: any) => api.get('/admin/revenue-metrics', { params }),
};

export const contactAPI = {
  submit: (data: any) => api.post('/contact/submit', data),
  getAll: (params?: any) => api.get('/contact', { params }),
  getById: (id: string) => api.get(`/contact/${id}`),
  updateStatus: (id: string, data: any) => api.patch(`/contact/${id}`, data),
  delete: (id: string) => api.delete(`/contact/${id}`),
  subscribe: (data: { email: string; name?: string }) => api.post('/contact/newsletter/subscribe', data),
};

export const settingsAPI = {
  getAll: (params?: any) => api.get('/settings', { params }),
  getAllAdmin: () => api.get('/settings/admin/all'),
  update: (data: any) => api.put('/settings/admin', data),
  delete: (id: string) => api.delete(`/settings/admin/${id}`),
  getCurrencies: () => api.get('/settings/currencies'),
};

export const businessProfileAPI = {
  getMyProfile: () => api.get('/business-profile/me'),
  create: (data: any) => api.post('/business-profile', data),
  update: (data: any) => api.put('/business-profile', data),
  getAll: () => api.get('/business-profile/all'),
};

export default api;
